import React, { createContext, useContext, useEffect, useState } from 'react';
import { Profile, Locale, Theme } from '../types';

// Storage keys
const AUTH_STORAGE_KEY = 'marryplan_auth';

interface StoredAuth {
  access_token: string;
  refresh_token: string;
  user: {
    id: string;
    username: string;
    email: string | null;
    platform_role?: string;
    is_super_admin?: boolean;
  };
}

interface AuthContextType {
  user: any;
  profile: Profile | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isSuperAdmin: boolean;
  accessToken: string | null;
  signOut: () => void;
  signIn: (username: string, password: string) => Promise<{ error?: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Helper to get stored auth
function getStoredAuth(): StoredAuth | null {
  try {
    const stored = localStorage.getItem(AUTH_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('Failed to parse stored auth:', e);
  }
  return null;
}

// Helper to store auth
function storeAuth(auth: StoredAuth): void {
  localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(auth));
}

// Helper to clear auth
function clearAuth(): void {
  localStorage.removeItem(AUTH_STORAGE_KEY);
  localStorage.removeItem('active_wedding_id');
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<any>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize from localStorage on mount
  useEffect(() => {
    const storedAuth = getStoredAuth();
    
    if (storedAuth && storedAuth.access_token && storedAuth.user) {
      // Restore user session from localStorage
      setUser({ id: storedAuth.user.id, email: storedAuth.user.email });
      setProfile({
        id: storedAuth.user.id,
        username: storedAuth.user.username,
        email: storedAuth.user.email,
        name: storedAuth.user.username,
        language: 'cs' as Locale,
        theme: 'system' as Theme,
      });
      setAccessToken(storedAuth.access_token);
      setIsSuperAdmin(storedAuth.user.is_super_admin === true);
    }
    
    // Always stop loading after checking localStorage
    setIsLoading(false);
  }, []);

  // Po obnovení Supabase tokenů (např. před nahráním fotky pozvánky) sjednotit React stav s localStorage
  useEffect(() => {
    const onTokensRefreshed = () => {
      const stored = getStoredAuth();
      if (stored?.access_token) setAccessToken(stored.access_token);
    };
    window.addEventListener('marryplan-auth-tokens-refreshed', onTokensRefreshed);
    return () => window.removeEventListener('marryplan-auth-tokens-refreshed', onTokensRefreshed);
  }, []);

  const signIn = async (username: string, password: string): Promise<{ error?: string }> => {
    // Normalize username
    const normalizedUsername = username.trim().toLowerCase();
    
    if (!normalizedUsername) {
      return { error: 'Uživatelské jméno je povinné' };
    }
    
    if (!password) {
      return { error: 'Heslo je povinné' };
    }

    try {
      // Call login API with timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);

      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: normalizedUsername, password }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // Check content type
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        console.error('Non-JSON response:', response.status);
        return { error: 'Chyba serveru. Zkuste to prosím později.' };
      }

      const data = await response.json();

      if (!response.ok) {
        return { error: data.error || 'Přihlášení selhalo' };
      }

      if (!data.access_token || !data.user) {
        return { error: 'Neplatná odpověď serveru' };
      }

      // Store auth in localStorage
      const authData: StoredAuth = {
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        user: data.user,
      };
      storeAuth(authData);

      // Update state
      setUser({ id: data.user.id, email: data.user.email });
      setProfile({
        id: data.user.id,
        username: data.user.username,
        email: data.user.email,
        name: data.user.username,
        language: 'cs' as Locale,
        theme: 'system' as Theme,
      });
      setAccessToken(data.access_token);
      setIsSuperAdmin(data.user.is_super_admin === true);

      return {};
    } catch (err: any) {
      if (err.name === 'AbortError') {
        return { error: 'Přihlášení trvá příliš dlouho. Zkuste to znovu.' };
      }
      console.error('SignIn error:', err);
      return { error: 'Chyba připojení. Zkontrolujte internet.' };
    }
  };

  const signOut = () => {
    clearAuth();
    setUser(null);
    setProfile(null);
    setAccessToken(null);
    setIsSuperAdmin(false);
    window.location.href = '/login';
  };

  const isAuthenticated = !!user && !!accessToken;

  return (
    <AuthContext.Provider value={{ 
      user, 
      profile, 
      isLoading, 
      isAuthenticated,
      isSuperAdmin,
      accessToken,
      signOut, 
      signIn 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
